import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import LoadingState from "../../../components/LoadingState/index.js";
import EmptyState from "../../../components/EmptyState/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import {
  queueMonitorService,
  type QueueOverview,
  type JobSummary,
  type JobDetail,
} from "../../../api/queueMonitor.service.js";
import styles from "./QueueMonitor.module.css";

const POLL_INTERVAL = 10000;
const PAGE_SIZE = 20;

const STATUS_OPTIONS = ["all", "waiting", "active", "completed", "failed", "delayed", "paused"];

function formatTimestamp(ts: number | null): string {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
}

function StatusBadge({ status }: { status: string }) {
  const classMap: Record<string, string> = {
    waiting: styles.statusWaiting,
    active: styles.statusActive,
    completed: styles.statusCompleted,
    failed: styles.statusFailed,
    delayed: styles.statusDelayed,
    paused: styles.statusPaused,
  };

  return (
    <span className={`${styles.statusBadge} ${classMap[status] || ""}`}>
      {status.toUpperCase()}
    </span>
  );
}

function QueueMonitorPage() {
  const [queues, setQueues] = useState<QueueOverview[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [, setJobsTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("failed");
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchQueues = useCallback(async () => {
    try {
      const result = await queueMonitorService.getQueuesOverview();
      setQueues(result.queues);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch queues"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    if (!selectedQueue) return;

    setJobsLoading(true);
    try {
      const result = await queueMonitorService.getQueueJobs(
        selectedQueue,
        statusFilter,
        page,
        PAGE_SIZE,
      );
      setJobs(result.jobs);
      setJobsTotal(result.total);
    } catch (err) {
      setJobs([]);
      setJobsTotal(0);
      setError(err instanceof Error ? err : new Error("Failed to fetch jobs"));
    } finally {
      setJobsLoading(false);
    }
  }, [selectedQueue, statusFilter, page]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  useEffect(() => {
    const interval = setInterval(fetchQueues, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchQueues]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleSelectQueue = useCallback((queueName: string) => {
    setSelectedQueue(queueName);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((status: string) => {
    setStatusFilter(status);
    setPage(1);
  }, []);

  const handleRowClick = useCallback(
    async (job: JobSummary) => {
      if (!selectedQueue) return;

      setIsModalOpen(true);
      setSelectedJob(null);

      try {
        const detail = await queueMonitorService.getJobDetails(selectedQueue, job.id);
        setSelectedJob(detail);
      } catch {
        setSelectedJob(null);
      }
    },
    [selectedQueue],
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedJob(null);
  }, []);

  const handleRetryJob = useCallback(async () => {
    if (!selectedQueue || !selectedJob) return;

    setActionLoading(true);
    try {
      await queueMonitorService.retryJob(selectedQueue, selectedJob.id);
      handleCloseModal();
      fetchJobs();
      fetchQueues();
    } catch {
      // Error is visible from the failed action
    } finally {
      setActionLoading(false);
    }
  }, [selectedQueue, selectedJob, handleCloseModal, fetchJobs, fetchQueues]);

  const handleRetryAllFailed = useCallback(async () => {
    if (!selectedQueue) return;
    if (!window.confirm(`Retry all failed jobs in "${selectedQueue}"?`)) return;

    setActionLoading(true);
    try {
      await queueMonitorService.retryAllFailed(selectedQueue);
      fetchJobs();
      fetchQueues();
    } catch {
      // Error is visible from the failed action
    } finally {
      setActionLoading(false);
    }
  }, [selectedQueue, fetchJobs, fetchQueues]);

  const handleClean = useCallback(
    async (cleanStatus: string) => {
      if (!selectedQueue) return;
      if (!window.confirm(`Clean all "${cleanStatus}" jobs from "${selectedQueue}"?`)) return;

      setActionLoading(true);
      try {
        await queueMonitorService.cleanQueue(selectedQueue, cleanStatus);
        fetchJobs();
        fetchQueues();
      } catch {
        // Error is visible from the failed action
      } finally {
        setActionLoading(false);
      }
    },
    [selectedQueue, fetchJobs, fetchQueues],
  );

  const columns: Column<JobSummary>[] = useMemo(
    () => [
      {
        header: "ID",
        accessor: "id" as keyof JobSummary,
        render: (value: unknown) => String(value),
      },
      {
        header: "Name",
        accessor: "name" as keyof JobSummary,
        render: (value: unknown) => String(value),
      },
      {
        header: "Status",
        accessor: "status" as keyof JobSummary,
        render: (value: unknown) => <StatusBadge status={value as string} />,
      },
      {
        header: "Created",
        accessor: "timestamp" as keyof JobSummary,
        render: (value: unknown) => formatTimestamp(value as number | null),
      },
      {
        header: "Attempts",
        accessor: "attemptsMade" as keyof JobSummary,
        render: (value: unknown) => String(value),
      },
      {
        header: "Failed Reason",
        accessor: "failedReason" as keyof JobSummary,
        render: (value: unknown) => {
          const reason = value as string | null;
          if (!reason) return "-";
          return reason.length > 80 ? `${reason.slice(0, 80)}...` : reason;
        },
      },
      {
        header: "",
        accessor: "id" as keyof JobSummary,
        render: (_value: unknown, row: JobSummary) => {
          if (row.status !== "failed") return null;
          return (
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={actionLoading}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (!selectedQueue) return;
                setActionLoading(true);
                queueMonitorService
                  .retryJob(selectedQueue, row.id)
                  .then(() => {
                    fetchJobs();
                    fetchQueues();
                  })
                  .finally(() => setActionLoading(false));
              }}
            >
              Retry
            </Button>
          );
        },
      },
    ],
    [selectedQueue, actionLoading, fetchJobs, fetchQueues],
  );

  const selectedQueueData = queues.find(q => q.name === selectedQueue);

  const status = (error as Error & { status?: number })?.status;
  const isAuthError = status === 401 || status === 403;

  if (isAuthError) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Queue Monitor" subtitle="Monitor and manage background job queues" />
        <UnauthorizedView />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Queue Monitor" subtitle="Monitor and manage background job queues" />

      <div className={styles.content}>
        {loading && <LoadingState message="Loading queues..." />}

        {error && !isAuthError && (
          <EmptyState
            variant="error"
            title="Failed to load queues"
            message={error.message}
            action={{
              label: "Retry",
              onClick: fetchQueues,
            }}
          />
        )}

        {!loading && !error && queues.length === 0 && (
          <EmptyState
            variant="empty"
            title="No queues found"
            message="No background job queues are available."
          />
        )}

        {!loading && !error && queues.length > 0 && (
          <>
            <div className={styles.overviewGrid}>
              {queues.map(queue => (
                <div
                  key={queue.name}
                  className={`${styles.queueCard} ${selectedQueue === queue.name ? styles.queueCardActive : ""}`}
                  onClick={() => handleSelectQueue(queue.name)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectQueue(queue.name);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <h4 className={styles.queueCardName}>{queue.name}</h4>
                  {queue.lastExecutedAt && (
                    <div className={styles.lastExecuted}>
                      Last run: {new Date(queue.lastExecutedAt).toLocaleString()}
                    </div>
                  )}
                  <div className={styles.countsGrid}>
                    <div className={styles.countItem}>
                      <span className={styles.countLabel}>Waiting</span>
                      <span className={styles.countValue}>{queue.counts.waiting}</span>
                    </div>
                    <div className={styles.countItem}>
                      <span className={styles.countLabel}>Active</span>
                      <span className={styles.countValue}>{queue.counts.active}</span>
                    </div>
                    <div className={styles.countItem}>
                      <span className={styles.countLabel}>Completed</span>
                      <span className={styles.countValue}>{queue.counts.completed}</span>
                    </div>
                    <div className={styles.countItem}>
                      <span className={styles.countLabel}>Failed</span>
                      <span
                        className={`${styles.countValue} ${queue.counts.failed > 0 ? styles.countFailed : ""}`}
                      >
                        {queue.counts.failed}
                      </span>
                    </div>
                    <div className={styles.countItem}>
                      <span className={styles.countLabel}>Delayed</span>
                      <span className={styles.countValue}>{queue.counts.delayed}</span>
                    </div>
                    <div className={styles.countItem}>
                      <span className={styles.countLabel}>Paused</span>
                      <span className={styles.countValue}>{queue.counts.paused}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.autoRefreshIndicator}>
              <span className={styles.refreshDot} />
              Auto-refreshing every 10s
            </div>

            {selectedQueue && (
              <>
                <h3 className={styles.sectionTitle}>Jobs: {selectedQueue}</h3>

                <div className={styles.toolbar}>
                  <div className={styles.statusFilters}>
                    {STATUS_OPTIONS.map(option => (
                      <button
                        key={option}
                        type="button"
                        className={`${styles.filterButton} ${statusFilter === option ? styles.filterButtonActive : ""}`}
                        onClick={() => handleStatusChange(option)}
                      >
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div className={styles.toolbarActions}>
                    {selectedQueueData && selectedQueueData.counts.failed > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRetryAllFailed}
                        disabled={actionLoading}
                      >
                        Retry All Failed
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => handleClean("completed")}
                      disabled={actionLoading}
                    >
                      Clean Completed
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => handleClean("failed")}
                      disabled={actionLoading}
                    >
                      Clean Failed
                    </Button>
                  </div>
                </div>

                <div className={styles.tableContainer}>
                  {jobsLoading && <LoadingState message="Loading jobs..." />}

                  {!jobsLoading && jobs.length === 0 && (
                    <EmptyState
                      variant="empty"
                      title="No jobs found"
                      message={`No ${statusFilter === "all" ? "" : statusFilter + " "}jobs in this queue.`}
                    />
                  )}

                  {!jobsLoading && jobs.length > 0 && (
                    <DataTable
                      data={jobs}
                      columns={columns}
                      onRowClick={handleRowClick}
                      rowKey="id"
                      size="small"
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={`Job: ${selectedJob?.id ?? "Loading..."}`}
        size="large"
        blurBackground
      >
        {!selectedJob ? (
          <LoadingState message="Loading job details..." />
        ) : (
          <div className={styles.modalContent}>
            <div className={styles.modalMeta}>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Status</span>
                <StatusBadge status={selectedJob.status} />
              </div>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Name</span>
                <span className={styles.metricValue}>{selectedJob.name}</span>
              </div>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Created</span>
                <span className={styles.metricValue}>{formatTimestamp(selectedJob.timestamp)}</span>
              </div>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Processed</span>
                <span className={styles.metricValue}>
                  {formatTimestamp(selectedJob.processedOn)}
                </span>
              </div>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Finished</span>
                <span className={styles.metricValue}>
                  {formatTimestamp(selectedJob.finishedOn)}
                </span>
              </div>
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Attempts</span>
                <span className={styles.metricValue}>{selectedJob.attemptsMade}</span>
              </div>
            </div>

            {selectedJob.failedReason && (
              <>
                <h4 className={styles.sectionTitle}>Failed Reason</h4>
                <pre className={styles.jsonViewer}>{selectedJob.failedReason}</pre>
              </>
            )}

            <h4 className={styles.sectionTitle}>Data</h4>
            <pre className={styles.jsonViewer}>{JSON.stringify(selectedJob.data, null, 2)}</pre>

            {selectedJob.returnValue !== null && selectedJob.returnValue !== undefined && (
              <>
                <h4 className={styles.sectionTitle}>Return Value</h4>
                <pre className={styles.jsonViewer}>
                  {JSON.stringify(selectedJob.returnValue, null, 2)}
                </pre>
              </>
            )}

            {selectedJob.stacktrace && selectedJob.stacktrace.length > 0 && (
              <div className={styles.stacktraceSection}>
                <h4 className={styles.sectionTitle}>Stack Trace</h4>
                <pre className={styles.stacktrace}>{selectedJob.stacktrace.join("\n")}</pre>
              </div>
            )}

            {selectedJob.status === "failed" && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleRetryJob}
                disabled={actionLoading}
              >
                Retry Job
              </Button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default QueueMonitorPage;
